import { Box, useTheme } from '@mui/material'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import Accordion from '@/components/Accordion'
import Button from '@/components/Button'
import DraggableContainer from '@/components/DraggableContainer'
import LabelWithTooltip from '@/components/LabelWithTooltip'
import LoadingSpinner from '@/components/LoadingSpinner'
import Select from '@/components/Select'
import { useAppSelector } from '@/store'
import { useGetMetadataQuery } from '@/store/apiService'
import { PLACEMENT } from '@/store/appSlice'
import {
  RecipeGenerationStatus,
  setRecipeGenerationStatus,
} from '@/store/sessionSlice'
import {
  removeCrownRecipeKey,
  setSelectedCrownKey,
  setSelectedCrownStainingBrand,
} from '@/store/stainingStudio'
import { getOptions } from '@/utils/helper'

import { CROWN_BUTTON_WITH_COLOR } from '../utils/constants'

interface StaingingStudioProps {
  dimensions: { width: number; height: number }
  setTooltipOpen: (tooltipOpen: boolean) => void
  setTooltipMessage: (tooltipMessage: string) => void
  selectedCategory: string | null
  setSelectedCategory: React.Dispatch<React.SetStateAction<string | null>>
}

export interface Metadata {
  [key: string]: any
}

const StaingingStudio: React.FC<StaingingStudioProps> = ({
  dimensions,
  setTooltipOpen,
  setTooltipMessage,
  selectedCategory,
  setSelectedCategory,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const dispatch = useDispatch()

  const PANEL_MIN_WIDTH = 778

  const selectedCrownKey = useAppSelector(
    (state) => state.stainingStudio.selectedCrownKey,
  )
  const selectedCrownStainingBrand = useAppSelector(
    (state) => state.stainingStudio.selectedCrownStainingBrand,
  )

  const crownRecipe = useAppSelector(
    (state) => state.stainingStudio.crownRecipe,
  )

  const selectedValues = useMemo(
    () =>
      [
        Object.keys(crownRecipe?.[PLACEMENT.LEFT] || {}),
        Object.keys(crownRecipe?.[PLACEMENT.RIGHT] || {}),
      ].flat(),
    [crownRecipe],
  )

  const {
    data: metadata,
    error,
    isLoading,
  } = useGetMetadataQuery(undefined, {
    refetchOnMountOrArgChange: true,
  }) as {
    data: Metadata | undefined
    error: any
    isLoading: boolean
  }

  const stainingBrands = useMemo(
    () => (metadata ? getOptions(metadata, 'available_stainingbrands') : []),
    [metadata],
  )

  if (error) {
    throw error
  }

  if (isLoading) {
    return <LoadingSpinner />
  }

  return (
    <DraggableContainer
      axis="x"
      bounds="parent"
      defaultPosition={{
        x: dimensions?.width ? dimensions?.width - PANEL_MIN_WIDTH / 2 : 0,
        y: dimensions?.height,
      }}
    >
      <Accordion
        title={
          <LabelWithTooltip
            titleSx={{ fontSize: '18px' }}
            label={t('stainingCorrection.panel.generateRecipe')}
            tooltipText={t('stainingCorrection.panel.recipeTooltip')}
          />
        }
        sx={{
          background: theme.palette.common.black,
          minWidth: `${PANEL_MIN_WIDTH}px`,
          transform: 'translate(0%, -100%)',
          '&.MuiPaper-root': {
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
          },
        }}
        accordionSummarySx={{
          padding: '8px 16px',
        }}
        titleSx={{
          fontSize: '20px',
        }}
        accordionDetailsSx={{
          padding: '16px 32px 24px 32px',
          maxHeight: '190px',
        }}
      >
        <Box display="flex" flexGrow={1} gap={4}>
          <Box display="flex">
            <Select
              options={stainingBrands}
              title={t('stainingCorrection.panel.stainingBrand')}
              tooltipText={t('stainingCorrection.panel.stainingBrandTooltip')}
              name="staining-brands"
              onFormValueChange={(name, value) => {
                dispatch(setSelectedCrownStainingBrand(value))
              }}
              defaultValue={selectedCrownStainingBrand}
              sx={{
                width: '250px',
              }}
              titleSx={{ fontSize: '20px' }}
              titleSize="small"
            />
          </Box>
          <Box
            sx={{
              borderRight: '1px solid var(--color-border-table)',
              height: 'auto',
            }}
            height="auto"
            display="flex"
          ></Box>

          <Box display="flex" flexGrow={1} gap={2} flexDirection={'column'}>
            <Box display="flex" flexDirection="column">
              <LabelWithTooltip
                label="Sections"
                tooltipText={
                  'Select a section to generate a recipe.\nThen, choose an option below and click twice on the canvas:\n1. First, click the target tooth.\n2. Then, click the crown on the aesthetic model to generate the recipe.'
                }
                titleSx={{ fontSize: '16px' }}
                tooltipSx={{ height: '20px', width: '20px' }}
                fontSize="16px"
                tooltipWidth={422}
              />
              <Select
                options={Object.keys(CROWN_BUTTON_WITH_COLOR).map((key) => ({
                  text: key,
                  value: key,
                }))}
                name="category-select"
                onFormValueChange={(name, value) => {
                  setSelectedCategory(value)
                }}
                defaultValue={selectedCategory}
                sx={{ width: `250px` }}
                titleSize="small"
              />
            </Box>

            {selectedCategory && (
              <Box display="flex" gap={1}>
                {Object.keys(CROWN_BUTTON_WITH_COLOR[selectedCategory]).map(
                  (name) => (
                    <Button
                      disableRipple
                      variant="contained"
                      key={name}
                      sx={{
                        maxWidth: `128px`,
                        whiteSpace: 'nowrap',
                        flexGrow: 1,
                        color:
                          selectedCrownKey === name ||
                          selectedValues?.includes(name)
                            ? theme.palette.common.black
                            : theme.palette.common.white,
                        fontWeight: 400,
                        background:
                          selectedCrownKey === name ||
                          selectedValues?.includes(name)
                            ? CROWN_BUTTON_WITH_COLOR[selectedCategory][name]
                            : '#292929',
                        '&:hover': {
                          background:
                            CROWN_BUTTON_WITH_COLOR[selectedCategory][name],
                          color: theme.palette.common.black,
                        },
                        fontSize: '14px',
                        maxHeight: '32px',
                      }}
                      label={name}
                      onClick={() => {
                        if (selectedValues?.includes(name)) {
                          dispatch(setSelectedCrownKey(null))
                          dispatch(removeCrownRecipeKey({ key: name }))
                          dispatch(
                            setRecipeGenerationStatus(
                              RecipeGenerationStatus.DEFAULT,
                            ),
                          )
                        } else {
                          dispatch(
                            setRecipeGenerationStatus(
                              RecipeGenerationStatus.GENERATE,
                            ),
                          )
                          dispatch(setSelectedCrownKey(name))
                          setTooltipOpen(true)
                          setTooltipMessage(
                            t('stainingCorrection.panel.firstStep'),
                          )
                        }
                      }}
                    />
                  ),
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Accordion>
    </DraggableContainer>
  )
}

export default StaingingStudio
